<template>
  <v-dialog v-model="visibility" persistent max-width="952px" max-height="550px">
    <ChangesActionDialog />

    <v-card flat class="pa-3 rounded-xl">
      <v-fade-transition hide-on-leave>
        <!-- Device list -->
        <div v-if="page === 0">
          <v-row no-gutters class="dialog-title pa-3" align="center">
            {{ t('$vuetify.__Approval.change_approval') }}
            <v-spacer></v-spacer>
            <span class="grey-small-text pr-1">{{ t('$vuetify.__Approval.last_update') }}:</span>
            <v-btn variant="text" size="small" icon color="#1b65f2" @click="store.dispatch('fetchApprovalRequestList')"><v-icon size="24">mdi-refresh</v-icon></v-btn>
          </v-row>

          <v-row no-gutters v-if="loading" class="py-3" align="center" justify="center">
            <v-progress-circular :size="50" color="#1b65f2" indeterminate></v-progress-circular>
          </v-row>
          <v-row no-gutters v-else>
            <v-col cols="6" v-for="approval_request in approval_request_list" :key="approval_request.approval_id" class="pa-3">
              <v-card flat class="changes-card pa-4">
                <v-row no-gutters class="pb-5" align="center">
                  <v-btn variant="text" size="small" color="#1b65f2" class="text-none px-2 ml-n2" @click="selectDevice(approval_request.approval_id)">
                    <v-icon class="pr-2">mdi-cellphone-link</v-icon>
                    {{ approval_request.device_name }}
                  </v-btn>
                  <v-spacer></v-spacer>
                  <span class="grey-small-desc">{{ approval_request.last_update_datetime?.replace('T', ' ') }}</span>
                </v-row>

                <v-row no-gutters>
                  <v-col cols="auto">
                    <span class="device-number-label">
                      <span class="device-number-text pr-2">{{ approval_request.num_changes }}</span>
                      <span class="device-changes-text">{{ t('$vuetify.__Approval.changes') }}</span>
                    </span>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <!-- Action buttons -->
          <v-row no-gutters class="pa-3">
            <v-spacer></v-spacer>
            <v-btn color="#1b65f2" rounded class="text-capitalize px-3" @click="store.commit('RESET_CHANGES_APPROVAL_DIALOG')">
              {{ t('$vuetify.__General.done') }}
            </v-btn>
          </v-row>
        </div>

        <!-- Changes detail -->
        <div v-if="page === 1">
          <!-- Back btn -->
          <v-row no-gutters class="ma-3">
            <v-col cols="12" class="pa-0">
              <v-btn variant="text" size="small" color="#1B65F2" class="text-capitalize ml-n2 px-3" @click="back()">
                <v-icon left>mdi-arrow-left</v-icon>
                {{ t('$vuetify.__General.back') }}
              </v-btn>
            </v-col>
          </v-row>

          <!-- Loading -->
          <v-row no-gutters v-if="loading" class="pt-3 pb-6" align="center" justify="center">
            <v-progress-circular :size="50" color="#1b65f2" indeterminate></v-progress-circular>
          </v-row>

          <!-- Title -->
          <v-row no-gutters v-else class="px-3 py-2">
            <v-col cols="12" class="pb-5">
              <v-row no-gutters align="center" class="dialog-title">
                <span class="pb-1">{{ t('$vuetify.__Approval.apostrophe_s_changes', changes_detail.device_name) }}</span>
                <span class="changes-number-text ml-4 px-2 py-1" style="color: #414245; background: rgba(0, 0, 0, 0.08)">{{ changes_detail.diff.length }}</span>

                <v-spacer></v-spacer>

                <v-btn :disabled="is_submit_btn_disable" rounded color="#1b65f2" class="text-none white--text mb-1" @click="confirm">
                  {{ mode === 'admin' ? t('$vuetify.__Approval.confirm_changes') : t('$vuetify.__Approval.submit_to_admin') }}
                </v-btn>
              </v-row>
            </v-col>
            <!-- Action bar -->
            <v-col cols="12" class="action-bar px-4" :style="table.selected_changes.length > 0 ? 'background: #1b65f2' : 'background: #f5f5f5'">
              <v-row no-gutters v-if="table.selected_changes.length > 0" align="center" style="color: white; height: 55px">
                <span class="content-text">{{ t('$vuetify.__Approval.selected', table.selected_changes.length) }}</span>
                <v-divider vertical class="pl-4 mr-4" style="border-color: #ffffff"></v-divider>
                <v-btn variant="text" size="small" class="text-none white--text px-2" @click="approveChanges()">
                  <v-icon>mdi-check</v-icon>
                  {{ t('$vuetify.__Approval.approve') }}
                </v-btn>
                <v-btn variant="text" size="small" class="text-none white--text" @click="cancelApproveChanges()">
                  <v-icon>mdi-close</v-icon>
                  {{ t('$vuetify.__Approval.cancel_approval') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn variant="text" size="small" icon color="#ffffff" @click="table.selected_changes = []"><v-icon>mdi-close-circle-outline</v-icon></v-btn>
              </v-row>

              <v-row no-gutters v-else align="center" style="height: 55px">
                <span class="black-subtitle-text">{{ mode === 'admin' ? t('$vuetify.__Approval.approve') : t('$vuetify.__Approval.approved_by_admin') }}</span>
                <span class="changes-number-text ml-2 px-2 py-1" style="color: #399617; background: rgba(57, 150, 23, 0.05)">{{ approve_num }}</span>
                <v-divider vertical class="pl-4 mr-4"></v-divider>
                <span class="black-subtitle-text">{{ mode === 'admin' ? t('$vuetify.__Approval.not_approve') : t('$vuetify.__Approval.unapproved_changes') }}</span>
                <span class="changes-number-text ml-2 px-2 py-1" style="color: #cf321d; background: rgba(207, 50, 29, 0.05)">{{ not_approved_num }}</span>
              </v-row>
            </v-col>

            <!-- Changes Table -->
            <v-col cols="12">
              <v-data-table
                :loading="changes_detail_loading ? '#1b65f2' : false"
                :headers="mode === 'editor' ? table.editor_headers : table.admin_headers"
                :items="changes_detail.diff"
                :item-value="(item) => item"
                :show-select="mode === 'admin' ? true : false"
                :item-class="approvedRow"
                :footer-props="{ 'show-first-last-page': true }"
                v-model="table.selected_changes"
                @click:row="clickChange"
                @toggle-select-all="selectAllDiff"
                @update:options="tablePaginationUpdated"
              >
                <template v-slot:[`header.data-table-select`]="{ allSelected, selectAll, someSelected }">
                  <v-checkbox-btn color="#1b65f2" :indeterminate="someSelected && !allSelected" :model-value="allSelected" @update:model-value="selectAll(!allSelected)"></v-checkbox-btn>
                </template>

                <template v-slot:[`item.data-table-select`]="{ internalItem, isSelected, toggleSelect }">
                  <v-checkbox-btn color="#1b65f2" :model-value="isSelected(internalItem)" @update:model-value="toggleSelect(internalItem)"></v-checkbox-btn>
                </template>
                <!-- New Version -->
                <template v-slot:[`item.target_value`]="{ item }">
                  <v-row no-gutters align="center">
                    <v-col cols="auto">{{ item.target_value }}</v-col>
                    <v-spacer></v-spacer>
                    <v-col cols="auto" v-if="item.status === 'approved'">
                      <span class="approved-label"><v-icon color="#399617" size="18">mdi-check</v-icon></span>
                    </v-col>
                  </v-row>
                </template>

                <!-- Changed by -->
                <template v-slot:[`item.changed_by_username`]="{ item }" v-if="mode === 'editor'">
                  <v-row no-gutters align="center">
                    <v-col cols="12"><v-icon size="18">mdi-account</v-icon>{{ item.changed_by_username }}</v-col>
                  </v-row>
                </template>

                <!-- Date/Time -->
                <template v-slot:[`item.changed_at`]="{ item }" v-if="mode === 'editor' || mode === 'admin'">
                  <v-row no-gutters v-if="item.changed_at">
                    <v-col cols="12">{{ item.changed_at.split('T')[0] }}</v-col>
                    <v-col cols="12">{{ item.changed_at.split('T')[1] }}</v-col>
                  </v-row>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </div>
      </v-fade-transition>
    </v-card>
  </v-dialog>
</template>

<script setup>
import ChangesActionDialog from '@/components/Dialogs/ChangesActionDialog'

import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
const store = useStore()
import { useLocale } from 'vuetify'
const { t } = useLocale()

const approval_status_changed = ref(false)

const visibility = computed(() => store.state.changesApprovalDialog.visibility)
const loading = computed(() => store.state.changesApprovalDialog.loading)
const changes_detail_loading = computed(() => store.state.changesApprovalDialog.changes_detail_loading)
const mode = computed(() => store.state.changesApprovalDialog.mode)
const page = computed({
  get() {
    return store.state.changesApprovalDialog.page
  },
  set(value) {
    store.commit('SET_CHANGES_APPROVAL_DIALOG_PAGE', value)
  }
})
const approval_request_list = computed(() => store.state.changesApprovalDialog.approval_request_list)
const changes_detail = computed(() => store.state.changesApprovalDialog.changes_detail)
const table = computed(() => store.state.changesApprovalDialog.table)
const is_submit_btn_disable = computed(() => store.state.changesApprovalDialog.is_submit_btn_disable)
const approve_num = computed(() => {
  let num = 0
  if (mode.value === 'editor') num = changes_detail.value.approved
  else if (mode.value === 'admin') {
    changes_detail.value.diff.forEach((change) => {
      if (change.status === 'approved') num++
    })
  }
  return num
})
const not_approved_num = computed(() => {
  let num = 0
  if (mode.value === 'editor') num = changes_detail.value.not_approved
  else if (mode.value === 'admin') {
    changes_detail.value.diff.forEach((change) => {
      if (change.status !== 'approved') num++
    })
  }
  return num
})

const tablePaginationUpdated = (options) => {
  if (mode.value === 'admin') store.dispatch('fetchApprovalRequestDetail', { approval_id: changes_detail.value.approval_id, options: options })
}

const selectDevice = (approval_id) => {
  store.commit('SET_CHANGES_APPROVAL_DIALOG_CHANGES_DETAIL', { device_id: null, device_name: '', approval_id: null, diff: [] })
  store.commit('SET_CHANGES_APPROVAL_DIALOG_CHANGES_DETAIL_APPROVAL_ID', approval_id)
  page.value = 1
}
const back = () => {
  if (mode.value === 'editor') store.commit('RESET_CHANGES_APPROVAL_DIALOG')
  else {
    if (approval_status_changed.value) store.commit('INIT_CHANGES_ACTION_DIALOG', { type: 'discard' })
    else page.value = 0
  }
}
const confirm = () => {
  if (mode.value === 'admin') store.dispatch('confirmApprovalRequest')
  else store.commit('INIT_CHANGES_ACTION_DIALOG', { type: 'submit', item: { device_name: changes_detail.value.device_name } })
}
const clickChange = (event, { item }) => {
  if (mode.value === 'admin') {
    let index = table.value.selected_changes.findIndex((change) => change.change_id === item.change_id)
    if (index >= 0) table.value.selected_changes.splice(index, 1)
    else table.value.selected_changes.push(item)
  }
}
const approveChanges = () => {
  changes_detail.value.diff.map((change) => {
    if (table.value.selected_changes.some((selected) => selected === change)) change.status = 'approved'
  })
  table.value.selected_changes = []
  approval_status_changed.value = true
}
const cancelApproveChanges = () => {
  changes_detail.value.diff.map((change) => {
    if (table.value.selected_changes.some((selected) => selected === change)) change.status = 'rejected'
  })
  table.value.selected_changes = []
  approval_status_changed.value = true
}
const approvedRow = (item) => {
  return item.status === 'approved' ? 'approved-row' : ''
}
const selectAllDiff = (e) => {
  if (e.value === true) {
    table.value.selected_changes = changes_detail.value.diff
  } else {
    table.value.selected_changes = []
  }
}
watch(page, () => {
  table.value.selected_changes = []
  approval_status_changed.value = false
  if (page.value === 0 && visibility.value) store.dispatch('fetchApprovalRequestList')
})
</script>

<style>
.approved-row {
  background: rgba(57, 150, 23, 0.08) !important;
}
</style>

<style scoped>
.changes-card {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  background: #fff;
}

.device-number-label {
  color: rgba(0, 0, 0, 0.6);
  background: rgba(0, 0, 0, 0.05);
  padding: 4px 6px;
}

.device-number-text {
  font-size: 20px;
  font-weight: 700;
  line-height: 125%;
}

.device-changes-text {
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.3px;
}

.changes-number-text {
  border-radius: 4px;
  font-size: 20px;
  font-weight: 700;
  line-height: 125%;
}

.action-bar {
  border-radius: 16px 16px 0px 0px;
}

:deep(.v-data-table tbody tr.v-data-table__selected),
:deep(.v-data-table tbody tr.v-data-table__selected:hover) {
  background: rgba(27, 101, 242, 0.08) !important;
}

.approved-label {
  padding: 4px 8px;
  border-radius: 4px;
  background: var(--success-1, rgba(57, 150, 23, 0.08));
}

:deep(.v-data-table) {
  height: 100%;
}

:deep(.v-data-table__wrapper) {
  max-height: calc(90vh - 300px);
  overflow-y: auto;
}
</style>
